import { Link } from "react-router-dom";
import { ArrowRight, Brain, Map, FileText, HeartHandshake, Clock, Lock, Users, HardHat, Sparkles } from "lucide-react";

const SECTIONS = [
  "Logical & Analytical Reasoning", "Verbal & Structural Comprehension", "Numerical & Statistical Aptitude",
  "Spatial & Structural Visualization", "Mechanical & Physical Systems", "Memory & Attentional Focus",
  "General Knowledge & Domain Familiarity", "Career Interests (RIASEC)", "Work Style & Social Interaction",
  "Values, Environment & Self-Knowledge",
];

const STEPS = [
  { icon: Brain, title: "Test your mind", text: "122 questions across 10 sections measure reasoning types, aptitudes, knowledge bases, interests and work style." },
  { icon: Map, title: "See your career map", text: "140+ careers in 15 disciplinary clusters, including trades, healthcare, green jobs and remote work, connected in a mind map, each with the reasons it does or doesn't fit." },
  { icon: HardHat, title: "Get the real picture", text: "Public contact, how many people you'd work around, environment, gear, certifications, training and honest expectations." },
  { icon: FileText, title: "Pick a path, get steps", text: "Choose a career and get concrete next steps to find a position, regardless of your experience." },
];

export default function Landing() {
  return (
    <div>
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pt-14 sm:pt-20 pb-16 grid grid-cols-12 gap-6 lg:gap-10">
        <div className="col-span-12 lg:col-span-7 fade-up">
          <p className="overline-label mb-5">Free · No login · 35–45 minutes</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.02] text-[#1C242B]">
            What if you need a change?<br />
            <span className="font-serif-accent italic font-medium text-[#D96B43]">Discover what you're made for.</span>
          </h1>
          <p className="mt-7 text-base sm:text-lg leading-relaxed text-slate-600 max-w-xl">
            An extensive, free career &amp; aptitude test that reveals your strengths, the kinds of intelligence you possess, and how
            they translate into real careers, with a detailed report and a practical plan to get there.
          </p>
          <div className="mt-9 flex flex-wrap items-center gap-4">
            <Link to="/assessment" data-testid="hero-start-test-btn"
              className="group inline-flex items-center gap-2 rounded-full bg-[#1C242B] text-white px-7 py-4 font-semibold hover:bg-[#D96B43] transition-colors">
              Begin the assessment <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
            </Link>
            <Link to="/careers" data-testid="hero-explore-link" className="rounded-full border border-[#1C242B]/15 px-5 py-3 text-sm font-semibold hover:bg-[#1C242B]/5 transition-colors">Explore careers</Link>
            <a href="#how" data-testid="hero-how-link" className="text-[#3E6B5C] font-medium hover:underline">See how it works</a>
          </div>
          <div className="mt-10 flex flex-wrap gap-6 text-sm text-slate-600">
            <span className="flex items-center gap-2"><Clock size={16} className="text-[#D96B43]" /> Save &amp; resume anytime</span>
            <span className="flex items-center gap-2"><Lock size={16} className="text-[#D96B43]" /> Private result link</span>
            <span className="flex items-center gap-2"><HeartHandshake size={16} className="text-[#D96B43]" /> $0 PDF always available</span>
          </div>
        </div>
        <div className="col-span-12 lg:col-span-5 relative fade-up" style={{ animationDelay: "0.15s" }}>
          <div className="rounded-3xl overflow-hidden border border-[#1C242B]/10 shadow-xl aspect-[4/5] relative">
            <img src="https://images.unsplash.com/photo-1573497701240-345a300b8d36?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
              alt="Person at work" className="w-full h-full object-cover" />
            <div className="absolute inset-x-4 bottom-4 rounded-2xl backdrop-blur-xl bg-white/85 border border-white/60 p-5">
              <p className="overline-label">Sample insight</p>
              <p className="mt-2 font-display font-semibold text-[#1C242B]">Holland code: I·R·C · Spatial 92 · Mechanical 83</p>
              <p className="text-sm text-slate-600 mt-1">Top cluster: Engineering &amp; Technical, with Few people · Low public contact</p>
            </div>
          </div>
          <div className="hidden sm:block absolute -left-6 top-10 rounded-2xl bg-[#3E6B5C] text-white px-5 py-4 shadow-lg rotate-[-4deg]">
            <p className="font-mono-ui text-xs opacity-80">10 SECTIONS</p>
            <p className="font-display text-2xl font-bold">122 questions</p>
          </div>
        </div>
      </section>

      <section id="how" className="max-w-7xl mx-auto px-5 sm:px-8 py-16">
        <p className="overline-label mb-3">How it works</p>
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#1C242B] max-w-2xl">From raw ability to a real next step.</h2>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {STEPS.map((s, i) => (
            <div key={s.title} data-testid={`how-step-${i + 1}`} className="lift rounded-2xl bg-white border border-[#1C242B]/10 p-7">
              <s.icon className="text-[#D96B43]" size={26} />
              <p className="font-mono-ui text-xs text-slate-400 mt-6">0{i + 1}</p>
              <h3 className="font-display text-lg font-semibold mt-1">{s.title}</h3>
              <p className="text-sm text-slate-600 mt-2 leading-relaxed">{s.text}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16 grid grid-cols-12 gap-8">
        <div className="col-span-12 lg:col-span-4">
          <p className="overline-label mb-3">The 10 sections</p>
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">Six aptitude tests, one knowledge sampler, three self-reflections.</h2>
          <p className="mt-4 text-slate-600">Aptitude sections have right answers. Interest, work-style and values sections have none; just be honest.</p>
        </div>
        <ol className="col-span-12 lg:col-span-8 grid sm:grid-cols-2 gap-x-8">
          {SECTIONS.map((s, i) => (
            <li key={s} className="flex items-baseline gap-4 py-4 border-b border-[#1C242B]/10">
              <span className="font-mono-ui text-sm text-[#D96B43]">{String(i + 1).padStart(2, "0")}</span>
              <span className="font-display font-medium text-[#1C242B]">{s}</span>
            </li>
          ))}
        </ol>
      </section>

      <section id="mission" className="max-w-7xl mx-auto px-5 sm:px-8 py-16">
        <div className="rounded-3xl bg-[#1C242B] text-[#FAF8F5] p-8 sm:p-14 grid grid-cols-12 gap-8">
          <div className="col-span-12 lg:col-span-7">
            <p className="font-mono-ui text-xs tracking-[0.2em] text-[#F0A584]">OUR MISSION</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mt-3">A humanitarian tool, free for everyone.</h2>
            <p className="mt-5 text-slate-300 leading-relaxed">
              Career guidance shouldn't be a luxury. The full test, results, career map and on-screen report are free forever.
              If you'd like the PDF, you can pay what you want, including $0. Contributions keep the lights on for the next person.
            </p>
          </div>
          <div className="col-span-12 lg:col-span-5 grid gap-4">
            <div className="rounded-2xl border border-white/15 p-5 flex gap-4">
              <Users className="text-[#F0A584] shrink-0" />
              <div><p className="font-display font-semibold">Static report: pay what you want</p><p className="text-sm text-slate-400">$0 allowed · $5 suggested</p></div>
            </div>
            <div className="rounded-2xl border border-white/15 p-5 flex gap-4">
              <Sparkles className="text-[#F0A584] shrink-0" />
              <div><p className="font-display font-semibold">Optional AI deep analysis</p><p className="text-sm text-slate-400">From $3 to cover AI running costs · $7 suggested</p></div>
            </div>
            <Link to="/assessment" data-testid="mission-start-test-btn" className="rounded-full bg-[#D96B43] text-white text-center px-6 py-3.5 font-semibold hover:bg-[#C05630] transition-colors">
              Start free now
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
